import React from 'react';
import { ButtonV2, Flex, FormControl, InputV2, TextV2 } from '@withjoy/joykit';
import { useFindInviteFormController } from './FindInviteForm.controller';
import { StyledContent, StyledFormStack, StyledHeaderStack, StyledInputsStack, StyledTitleStack, styles } from '../UnlockForm/UnlockForm.styles';
import { StyledInputWrapper } from '../shared/UnlockInputWrapper.styles';
import type { GuestTypes } from '@apps/guest/packages/layout-engine/components/UnlockDialog/UnlockDialog';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { body4ToSubHeadingOverride, hed6ToSubHeadingOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useDeferredInputFocus } from '../useDeferredInputFocus';
import { useUnlockDialogInputStyles, useUnlockDialogLinkStyles } from '../useUnlockDialogButtonStyles';
import { UnlockSubmitButton } from '../shared/UnlockSubmitButton';
import { useTranslation } from '@shared/core';

export interface FindInviteFormProps {
  eventId: string;
  setGuestsFound: (p: number) => void;
  setGuest: (p: GuestTypes) => void;
  onShowAskHost?: () => void;
}

export const FindInviteForm: React.FC<FindInviteFormProps> = ({ eventId, setGuestsFound, setGuest, onShowAskHost }) => {
  const {
    formik,
    loading,
    title,
    subtitle,
    firstNameLabel,
    lastNameLabel,
    firstNamePlaceholder,
    lastNamePlaceholder,
    submitButtonText,
    lastNameInputError,
    firstNameInputError,
    onSignInHandler,
  } = useFindInviteFormController({ eventId, setGuestsFound, setGuest });
  const { tq } = useTranslation('guestUnlock');
  const inputRef = useDeferredInputFocus();
  const inputStyles = useUnlockDialogInputStyles();
  const linkStyles = useUnlockDialogLinkStyles();

  return (
    <StyledContent
      __css={styles.content}
      data-testid="find-invite-form"
    >
      <StyledHeaderStack>
        <StyledTitleStack>
          <GuestSiteTypographyOverride override={hed6ToSubHeadingOverride}>
            <TextV2 typographyVariant="hed6">{title}</TextV2>
          </GuestSiteTypographyOverride>
          <GuestSiteTypographyOverride override={body4ToSubHeadingOverride}>
            <TextV2 typographyVariant="body4">{subtitle}</TextV2>
          </GuestSiteTypographyOverride>
          {onShowAskHost && (
            <Flex
              alignItems="center"
              gap={3}
            >
              <TextV2 typographyVariant="label3">{tq`Need help?`}</TextV2>
              <ButtonV2
                variant="ghost"
                intent="neutral"
                typographyVariant="label3"
                width="fit-content"
                height="fit-content"
                padding={0}
                borderBottom="1px solid"
                borderBottomColor="currentColor"
                borderBottomLeftRadius="0px"
                borderBottomRightRadius="0px"
                onClick={onShowAskHost}
                {...linkStyles}
              >
                {tq`Ask the Host`}
              </ButtonV2>
            </Flex>
          )}
        </StyledTitleStack>
      </StyledHeaderStack>
      <StyledFormStack
        spacing={6}
        __css={styles.formStack}
      >
        <form onSubmit={formik.handleSubmit}>
          <StyledInputsStack>
            <StyledInputWrapper $focusColor={inputStyles.focusColor}>
              <FormControl
                label={firstNameLabel}
                overrides={{ Label: { props: { color: 'inherit' } } }}
              >
                <InputV2
                  ref={inputRef}
                  overrides={{ Root: { props: { borderColor: inputStyles.borderColor } } }}
                  placeholder={firstNamePlaceholder}
                  isInvalid={(firstNameInputError && formik.touched.firstName) || undefined}
                  isDisabled={loading}
                  {...formik.getFieldProps('firstName')}
                />
              </FormControl>
            </StyledInputWrapper>
            <StyledInputWrapper $focusColor={inputStyles.focusColor}>
              <FormControl
                label={lastNameLabel}
                overrides={{ Label: { props: { color: 'inherit' } } }}
              >
                <InputV2
                  overrides={{ Root: { props: { borderColor: inputStyles.borderColor } } }}
                  placeholder={lastNamePlaceholder}
                  isInvalid={(lastNameInputError && formik.touched.lastName) || undefined}
                  isDisabled={loading}
                  {...formik.getFieldProps('lastName')}
                />
              </FormControl>
            </StyledInputWrapper>
          </StyledInputsStack>
          <UnlockSubmitButton
            width={'100%'}
            shape="rounded"
            variant="outline"
            type="submit"
            loading={loading}
          >
            {submitButtonText}
          </UnlockSubmitButton>

          <Flex
            justifyContent="center"
            marginTop={6}
          >
            <ButtonV2
              variant="ghost"
              intent="neutral"
              typographyVariant="button2"
              onClick={onSignInHandler}
              width="fit-content"
              height="fit-content"
              padding={0}
              borderBottom="1px solid"
              borderBottomColor="currentColor"
              borderBottomLeftRadius="0px"
              borderBottomRightRadius="0px"
              {...linkStyles}
            >
              {tq`Sign In`}
            </ButtonV2>
          </Flex>
        </form>
      </StyledFormStack>
    </StyledContent>
  );
};
